// The Game-Dev course island: the Godot bot standing on its platform,
// surrounded by floating crates. This is the one course with real art —
// the other two use PlaceholderIsland until their models exist.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAnimations } from '@react-three/drei';
import { createPortal, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import {
  Fitted,
  HaloRing,
  PLATFORM_TOP,
  ShadowBlob,
  findBone,
  pickIdleClip,
  useCloned,
  useClonedGLTF,
  usePointerCursor,
} from './helpers';

/** Crate layout, relative to the platform's top surface. */
const CRATES = [
  { offset: [1.65, 0, 0.9], rot: 0.4, size: 1.15, phase: 0 },
  { offset: [-2.15, 1.35, -0.5], rot: -0.8, size: 0.9, phase: 1.7 },
  { offset: [-1.5, 0, 1.5], rot: 1.15, size: 0.8, phase: 3.1 },
];

const CRATE_BURST_SECONDS = 0.86;

const Crate = ({ offset, rot, size, phase, top, reduced }) => {
  const object = useCloned('/GodotBotBox.glb');
  const group = useRef();
  const burst = useRef(0);
  const targetScale = useMemo(() => new THREE.Vector3(1, 1, 1), []);
  const [hovered, setHovered] = useState(false);
  const [reducedTurn, setReducedTurn] = useState(0);
  const baseY = top + offset[1];
  usePointerCursor(hovered);

  useFrame(({ clock }, delta) => {
    if (!group.current) return;

    if (reduced) {
      group.current.position.y = baseY;
      group.current.rotation.set(0, rot + reducedTurn * (Math.PI / 2), 0);
      group.current.scale.setScalar(hovered ? 1.06 : 1);
      return;
    }

    const t = clock.getElapsedTime() + phase;
    let jump = 0;
    let spin = 0;
    let kick = 0;

    if (burst.current > 0) {
      const progress = 1 - burst.current / CRATE_BURST_SECONDS;
      kick = Math.sin(progress * Math.PI);
      jump = kick * 0.72;
      spin = (1 - Math.pow(1 - progress, 3)) * Math.PI * 2;
      burst.current = Math.max(0, burst.current - delta);
    }

    group.current.position.y = baseY + Math.sin(t * 0.9) * 0.12 + jump;
    group.current.rotation.y = rot + Math.sin(t * 0.35) * 0.18 + spin;
    group.current.rotation.z = Math.sin(t * 0.55) * 0.035 + kick * 0.12;

    const scaleUp = 1 + (hovered ? 0.08 : 0) + kick * 0.1;
    targetScale.setScalar(scaleUp);
    group.current.scale.lerp(targetScale, 1 - Math.exp(-delta * 12));
  });

  const activate = (event) => {
    event.stopPropagation();
    if (reduced) {
      setReducedTurn((turn) => (turn + 1) % 4);
    } else if (burst.current <= 0) {
      burst.current = CRATE_BURST_SECONDS;
    }
  };

  return (
    <group
      ref={group}
      position={[offset[0], baseY, offset[2]]}
      rotation={[0, rot, 0]}
      onPointerOver={(event) => { event.stopPropagation(); setHovered(true); }}
      onPointerOut={() => setHovered(false)}
      onClick={activate}
    >
      <Fitted object={object} size={size} fit="max" />
    </group>
  );
};

/* ── Rifle ────────────────────────────────────────────────────────────────
   Parented to the right hand bone. Rather than hand-tuning an offset and a
   rotation I can't see, the pose supplies them: the idle holds the weapon with
   both hands, so the right hand is the pistol grip, the left is the foregrip,
   and the vector between them is the barrel. That also gives the scale, so the
   rifle stays proportional whatever height the bot is fitted to.          */

/** Right-hand-to-left-hand distance as a fraction of the rifle's length. */
const GRIP_SPAN = 0.52;
/** Where the right hand sits on the rifle, in rifle-lengths from its centre. */
const GRIP_ANCHOR = [-0.25, -0.15, 0.2];
/** The model lies along +X. Set true if it ends up pointing backwards
    (then negate GRIP_ANCHOR's first value too). */
const BARREL_FLIP = false;

const _pRight = new THREE.Vector3();
const _pLeft = new THREE.Vector3();
const _handScale = new THREE.Vector3();
const _botScale = new THREE.Vector3();
const _botQuat = new THREE.Quaternion();
const _dir = new THREE.Vector3();
const _up = new THREE.Vector3();
const _x = new THREE.Vector3();
const _y = new THREE.Vector3();
const _z = new THREE.Vector3();
const _basis = new THREE.Matrix4();
const _handInverse = new THREE.Matrix4();

const Rifle = ({ bot, botGroup }) => {
  const object = useCloned('/GodotRifle.glb');
  const holder = useRef();
  const [hands, setHands] = useState(null);

  useEffect(() => {
    const right = findBone(bot, 'mixamorig:RightHand');
    const left = findBone(bot, 'mixamorig:LeftHand');
    setHands(right && left ? { right, left } : null);
  }, [bot]);

  useFrame(() => {
    if (!hands || !holder.current || !botGroup.current) return;
    const { right, left } = hands;

    right.updateWorldMatrix(true, false);
    left.updateWorldMatrix(true, false);
    right.getWorldPosition(_pRight);
    left.getWorldPosition(_pLeft);
    right.getWorldScale(_handScale);
    botGroup.current.getWorldScale(_botScale);
    if (!_handScale.x || !_botScale.x) return;

    // The bone chain carries the armature's 0.01 export scale, so convert
    // hand-local units back to island units before sizing the rifle.
    const handToIsland = _botScale.x / _handScale.x;
    const gap = _pRight.distanceTo(_pLeft) / _botScale.x;
    holder.current.scale.setScalar(handToIsland * (gap / GRIP_SPAN));

    _dir.subVectors(_pLeft, _pRight);
    if (_dir.lengthSq() < 1e-8) return;
    _handInverse.copy(right.matrixWorld).invert();
    _x.copy(_dir).transformDirection(_handInverse).normalize();
    if (BARREL_FLIP) _x.negate();

    botGroup.current.getWorldQuaternion(_botQuat);
    _up.set(0, 1, 0).applyQuaternion(_botQuat).transformDirection(_handInverse).normalize();

    _z.crossVectors(_x, _up);
    if (_z.lengthSq() < 1e-6) _z.set(0, 0, 1); // barrel straight up — pick any roll
    _z.normalize();
    _y.crossVectors(_z, _x).normalize();
    _basis.makeBasis(_x, _y, _z);
    holder.current.quaternion.setFromRotationMatrix(_basis);
  });

  if (!hands) return null;

  return createPortal(
    <group ref={holder}>
      {/* Shift the rifle so the grip — not its centre — lands on the hand. */}
      <group position={[-GRIP_ANCHOR[0], -GRIP_ANCHOR[1], -GRIP_ANCHOR[2]]}>
        <Fitted object={object} size={1} fit="max" align="center" />
      </group>
    </group>,
    hands.right
  );
};

/** The bot itself — fitted to a fixed height and playing its idle clip. */
const Bot = ({ object, animations, top, reduced }) => {
  const group = useRef();
  const { actions } = useAnimations(animations, group);
  const idle = useMemo(() => pickIdleClip(animations), [animations]);
  const [posed, setPosed] = useState(false);
  const frames = useRef(0);

  useEffect(() => {
    const action = idle && actions[idle.name];
    if (!action) return undefined;
    // Full weight from frame one — no fadeIn. A fade ramps the clip's *weight*
    // from 0, and at weight 0 the mixer writes nothing, so the rig holds its
    // bind pose: a T-pose staring at the floor. Worse, the rifle is sized from
    // the gap between the hands, and a T-pose spreads them a full arm-span
    // apart, so it appears huge and shrinks as the arms close.
    action.reset().play();
    action.paused = reduced;
    return () => { action.stop(); };
  }, [actions, idle, reduced]);

  // Even at full weight the first frame is unusable: Rifle's useFrame is
  // registered before this component's (children mount first), so it reads the
  // bone matrices one frame behind. Stay hidden until the mixer has written a
  // real pose and the rifle has measured it — the group hides its whole
  // subtree, and the rifle is portaled into a bone inside it, so both wait.
  useFrame(() => {
    if (posed) return;
    frames.current += 1;
    if (frames.current >= 2) setPosed(true);
  });

  return (
    <group ref={group} position={[0, -0.2, 2]} visible={posed}>
      <Fitted object={object} size={1.2} />
      <Rifle bot={object} botGroup={group} />
    </group>
  );
};

const GodotIsland = ({ accent, accentInk, reduced }) => {
  const platform = useCloned('/GodotBotPlatform.glb');
  const { object: bot, animations } = useClonedGLTF('/GodotBot.glb');
  // Measured from the model rather than assumed, so a different platform
  // export doesn't leave the bot hovering or sunk into the deck.
  const [top, setTop] = useState(PLATFORM_TOP);

  return (
    <group>
      <Fitted object={platform} size={5} fit="max" onFit={({ height }) => setTop(height)} />
      <ShadowBlob radius={3.1} opacity={0.55} position={[0, top + 0.012, 0]} />
      <HaloRing accent={accent} accentInk={accentInk} radius={2.5} position={[0, top - 0.4, 0]} />

      <Bot object={bot} animations={animations} top={top} reduced={reduced} />

      {CRATES.map((c, i) => (
        <Crate key={i} {...c} top={top} reduced={reduced} />
      ))}
    </group>
  );
};

export default GodotIsland;
